<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col
        vs-w="8"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4 items-stretch">
          <div class="flex items-center w-full">
            <select-with-search
              v-model="filters.agency_id"
              placeholder="Select Agency"
              :options="agencyOptions"
            />
          </div>
          <div class="flex items-center w-full">
            <date-picker
              type="date"
              format="DD MMM YYYY"
              value-type="DD-MM-YYYY"
              placeholder="Start Date"
              v-model="filters.start_date"
            />
          </div>
          <div class="flex items-center w-full">
            <date-picker
              type="date"
              format="DD MMM YYYY"
              value-type="DD-MM-YYYY"
              placeholder="End Date"
              v-model="filters.end_date"
            />
          </div>
          <div class="flex items-center">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px;"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
          <div class="flex items-center">
            <vs-button
              type="filled"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 120px;"
              @click="setUrlParams"
              :disabled="!!!filters.agency_id || !!!filters.start_date || !!!filters.end_date"
            >
              View Report
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div
      id="mainContainer"
      class="main-container flex flex-col gap-y-5"
    >
      <div class="flex flex-row items-center justify-center">
        <vs-button
          :disabled="isBusy"
          :class="`button w-min font-semibold ml-5 ${isBusy && 'cursor-not-allowed opacity-50 animate-bounce'}`"
          color="secondary"
          @click="sendDocumentTrackingReportEmail"
        >
          Send Email
        </vs-button>
      </div>
      <template
        v-if="!ssrsUrl"
      >
        <div class="w-full flex flex-col items-center gap-y-3">
          <p class="text-2xl font-semibold">
            No Data Found
          </p>
          <p
            class="text-grey-001"
          >
            Select filter to show the report data
          </p>
        </div>
      </template>
      <template v-else>
        <div class="w-full h-full flex flex-col items-center">
          <iframe
            v-if="!!ssrsUrl"
            :src="ssrsUrl"
            class="border-0 relative h-full w-full"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import moment from "moment/moment";
import { mapGetters } from "vuex";

import DatePicker from '@/components/datePicker'
import SelectWithSearch from "@/components/selectWithSearch";
import { PAGE_TITLE } from "@/fixtures/Constants";

export default {
  metaInfo() {
    return {
      title:  `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    DatePicker,
    SelectWithSearch
  },
  data() {
    const defaultStart = moment().startOf('month').format('DD-MM-YYYY')
    const defaultEnd = moment().endOf('month').format('DD-MM-YYYY')
    return {
      filters: {
        agency_id: null,
        start_date: defaultStart,
        end_date: defaultStart
      },
      agencyOptions: [],
      ssrsUrl: null,
      defaultStartDate: defaultStart,
      defaultEndDate: defaultEnd,
      isBusy: false
    }
  },
  computed: {
    ...mapGetters({
      agencies: 'AgencyManagement/agencies'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  async mounted() {
    if (this.$route.query.start_date || this.$route.query.end_date) {
      try {
        const startDate = this.$route.query.start_date
        const endDate = this.$route.query.end_date
        
        if (startDate && endDate) {
          const parsedStartDate = moment(startDate, 'YYYY-MM-DD')
          const parsedEndDate = moment(endDate, 'YYYY-MM-DD')
          
          if (parsedStartDate.isValid() && parsedEndDate.isValid()) {
            this.filters.start_date = parsedStartDate.format('DD-MM-YYYY')
            this.filters.end_date = parsedEndDate.format('DD-MM-YYYY')
          } else {
            this.showFailedNotification(this, 'Invalid date parameter format, please try again')
          }
        } else {
          this.showFailedNotification(this, 'Both start_date and end_date are required')
        }
      } catch (e) {
        this.showFailedNotification(this, 'Invalid date parameter format, please try again')
      }
    }
    this.fetchAgency()
  },
  watch: {
    agencies: {
      handler(val) {
        if (val) {
          this.agencyOptions = val.map(item => ({
            value: item.id,
            text: item.name
          }))
        }
      }
    }
  },
  methods: {
    async fetchAgency() {
      try {
        await this.$store.dispatch('AgencyManagement/fetchAgencies', {
          is_master_data: false,
          status: true,
          sort_dir: 'asc',
          sort_by: 'name'
        })
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch agencies, please try again')
      }
    },
    async setUrlParams() {
      const apiFilters = {
        ...this.filters,
        is_agency: false
      }
      const ssrsBaseUrl = `${process.env.VUE_APP_SSRS_URL}/DOCUMENT_TRACKING_REPORT`
      const searchParams = `${new URLSearchParams(apiFilters).toString()}`
      this.ssrsUrl = `${ssrsBaseUrl}&${searchParams}`
    },
    async sendDocumentTrackingReportEmail() {
      this.isBusy = true
      try {
        await this.$store.dispatch('DockingSchedule/postDocumentTrackingReportSendEmail', {
          ...(this.filters.agency_id ? { agency_id: this.filters.agency_id.toString() } : null),
          ...(this.filters.start_date ? { start_date: this.filters.start_date } : null),
          ...(this.filters.end_date ? { end_date: this.filters.end_date } : null)
        })

        this.showSuccessNotification(this, 'Document tracking report email successfully sent')
      } catch ({ data }) {
        const message = data?.error?.message || 'Failed to send document tracking report email, please try again'
        this.showFailedNotification(this, message)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filters = {
        agency_id: null,
        start_date: this.defaultStartDate,
        end_date: this.defaultEndDate
      }
      this.ssrsUrl = null
    }
  }
}
</script>

<style lang="scss">
.filter-container {
  @apply w-full p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.main-container {
  @apply w-full h-full p-8 mt-6 flex flex-row items-start bg-white rounded-lg;
}
</style>